import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SectionProps {
  /** Section content */
  children: ReactNode;
  /** Vertical spacing between section and siblings */
  spacing?: 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
  /** Optional id for anchors */
  id?: string;
}

/**
 * Semantic section wrapper with consistent vertical rhythm.
 *
 * Replaces ad-hoc `<section className="space-y-*">` patterns.
 * Provides standardized spacing between content elements.
 *
 * @example
 * ```tsx
 * <Section spacing="md">
 *   <SectionHeader title="Sites" />
 *   <div className="grid gap-4 sm:grid-cols-2">
 *     {sites.map(site => <NavigableEntityCard key={site.id} ... />)}
 *   </div>
 * </Section>
 * ```
 */
export function Section({ children, spacing = 'md', className, id }: SectionProps) {
  const spacingClass = {
    sm: 'space-y-4', // 16px
    md: 'space-y-6', // 24px
    lg: 'space-y-8', // 32px
  }[spacing];

  return (
    <section id={id} className={cn(spacingClass, className)}>
      {children}
    </section>
  );
}
